import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { get, ref } from 'firebase/database';
import { db } from '../../firebaseConfig';
import { useLanguage } from '../contexts/LanguageContext';
import { getScoutInventoryJob } from '../scoutApi';
import useOpportunityData, { useOpportunityAccess } from '../opportunities/useOpportunityData';
import { feedItemToCard } from '../opportunities/opportunityData';
import { JobListCard, JobTabs } from '../opportunities/OpportunityComponents';
import ScoutInventoryBrowser from '../components/ScoutInventoryBrowser';
import JobWorkspace from '../opportunities/JobWorkspace';

export default function JobSearchScreen({ user, profile }) {
  const { jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { language } = useLanguage();
  const access = useOpportunityAccess();
  const data = useOpportunityData(user, profile, language);
  const inventory = params.get('inventory') === '1';
  const view = inventory || !access.premium ? 'browse' : 'matches';
  const [resolved, setResolved] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const match = data.matches.find((item) => item.id === jobId);
  const saved = data.applications[jobId];
  const fromState = location.state?.job?.id === jobId ? location.state.job : null;
  const resolvedJob = resolved?.id === jobId ? resolved : null;
  const available = resolvedJob || fromState || match || (saved && { ...saved, id: jobId, jobOpening: saved.jobTitle });
  const job = available ? { ...available, ...(match ? { isMatch: true, fitScore: match.fitScore, reason: match.reason, concern: match.concern } : { isMatch: false }) } : null;

  useEffect(() => {
    const controller = new AbortController();
    setError(false); setLoading(true); setResolved(null);
    const load = async () => {
      try {
        // Read existing endpoints only. No new backend behavior is introduced.
        if (!inventory) {
          const snapshot = await get(ref(db, `job_posts/${jobId}`));
          if (controller.signal.aborted) return;
          if (snapshot.exists()) { setResolved({ ...snapshot.val(), id: jobId }); return; }
        }
        const response = await getScoutInventoryJob({ user, jobId, signal: controller.signal });
        if (!controller.signal.aborted) setResolved(feedItemToCard(response.job, response.scoutId, language));
      } catch {
        if (!controller.signal.aborted) setError(true);
      } finally { if (!controller.signal.aborted) setLoading(false); }
    };
    void load();
    return () => controller.abort();
  }, [jobId, inventory, user, language, retry]);
  const returnTo = location.state?.returnTo || (params.get('applied') === '1' ? '/opportunities?view=applied' : view === 'browse' ? '/opportunities?view=browse' : '/opportunities');
  const openJob = (next, browseState) => navigate(`/job/${encodeURIComponent(next.id)}${view === 'browse' ? '?inventory=1' : ''}`, { state: { job: next, browseState, returnTo: view === 'browse' ? '/opportunities?view=browse' : '/opportunities' } });
  return <JobWorkspace key={jobId} job={job} access={access} profile={profile} user={user} userId={user?.uid} documents={saved?.documents} application={saved} documentsReady={data.loaded}
    loading={loading} detailsError={error} error={error && !job} onRetry={() => setRetry((value) => value + 1)} returnTo={returnTo} returnState={{ browseState: location.state?.browseState }} initialStep={params.get('step') === 'outreach' ? 2 : params.get('step') === 'application' ? 1 : 0}
    upgradeDestination={(step) => { const next = new URLSearchParams(params); next.set('step', step === 2 ? 'outreach' : 'application'); return { path: `${location.pathname}?${next}`, state: location.state }; }}
    sidebar={<><div className="opp-feed-heading"><h2>{view === 'matches' ? 'Jobs found for you' : 'Search jobs'}</h2></div><JobTabs access={access} applied={false} value={view} user={user} profile={profile} onChange={(next) => setParams((current) => { if (next === 'browse') current.set('inventory', '1'); else current.delete('inventory'); return current; })} />
      {view === 'browse' ? <ScoutInventoryBrowser user={user} appliedJobIds={data.appliedJobIds} onJobClick={openJob} selectedJobId={jobId} compact initialState={location.state?.browseState} /> : <div className="opp-sidebar-jobs">{!data.loaded && <p role="status">Loading matches…</p>}{data.loaded && !data.visibleMatches.length && <p className="opp-muted">No matches yet. Explore Search All Jobs.</p>}{data.visibleMatches.map((item) => <JobListCard key={item.id} job={item} active={item.id === jobId} onClick={openJob} showMatch />)}</div>}</>} />;
}
